<template>
  <div class="col-12" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <div class="row g-2 align-items-center px-3 px-xl-4 pt-3">
      <div class="col-auto">
        <input
          type="month"
          class="form-control form-control-sm"
          v-model="monthFilter"
          @change="fetchRows"
        />
      </div>

      <div class="col-auto ms-auto d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm" @click="onPrint" :disabled="isLoading">
          <i class="fas fa-print me-1"></i>{{ $t('salary_management.print') }}
        </button>
        <button class="btn btn-outline-success btn-sm" @click="onExportExcel" :disabled="isLoading">
          <i class="fas fa-file-excel me-1"></i>{{ $t('salary_management.export_excel') }}
        </button>
        <button class="btn btn-outline-danger btn-sm" @click="onExportPdf" :disabled="isLoading || exportingPdf">
          <i class="fas fa-file-pdf me-1"></i>{{ $t('salary_management.export_pdf') }}
        </button>
      </div>
    </div>

    <div class="card mb-3 mb-xxl-5" style="margin-top: 2.5rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t('salary_management.teacher_sheet_title') }}
          </h6>
        </div>
      </div>

      <LoadingSpinner v-if="isLoading" :message="$t('common.loading')" class="py-5" />

      <template v-else>
        <!-- Desktop table -->
        <div class="table-responsive p-1 p-xl-4 d-none d-xl-block">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.teacher_name') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.job_title') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.bank_name') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.bank_branch') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.account_number') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.iban') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.total_lessons') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.attended_lessons') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.absent_lessons') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('salary_management.net_salary') }}</th>
                <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('salary_management.notes') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="rows.length === 0">
                <td colspan="11" class="text-center py-4 text-muted">
                  <i class="fas fa-file-invoice-dollar fa-2x mb-2"></i>
                  <p class="mb-0">{{ $t('salary_management.no_calculations') }}</p>
                </td>
              </tr>
              <tr v-for="r in rows" :key="r.id">
                <td><span class="text-sm">{{ r.model_name || '—' }}</span></td>
                <td><span class="text-sm">{{ r.job_title || '—' }}</span></td>
                <td><span class="text-sm">{{ r.bank_name || '—' }}</span></td>
                <td><span class="text-sm">{{ r.bank_branch || '—' }}</span></td>
                <td><span class="text-sm">{{ r.account_number || '—' }}</span></td>
                <td><span class="text-sm">{{ r.iban || '—' }}</span></td>
                <td class="text-center"><span class="text-sm">{{ r.total_lessons ?? '—' }}</span></td>
                <td class="text-center"><span class="text-sm">{{ r.attended_lessons ?? '—' }}</span></td>
                <td class="text-center"><span class="text-sm">{{ r.absent_lessons ?? '—' }}</span></td>
                <td class="text-center"><span class="text-sm fw-bold">{{ formatDisplay(r.net_salary) }}</span></td>
                <td><span class="text-sm text-muted">{{ r.notes || '—' }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="d-block d-xl-none">
          <div v-if="rows.length === 0" class="text-center py-4 text-muted">
            <i class="fas fa-file-invoice-dollar fa-2x mb-2"></i>
            <p class="mb-0">{{ $t('salary_management.no_calculations') }}</p>
          </div>
          <div v-for="r in rows" :key="'mobile-' + r.id" class="card mb-3 mx-2">
            <div class="card-body py-2">
              <h6 class="mb-1 text-sm">{{ r.model_name || '—' }}</h6>
              <p class="text-xs text-muted mb-1" v-if="r.job_title">{{ $t('salary_management.job_title') }}: {{ r.job_title }}</p>
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.bank_name') }}: {{ r.bank_name || '—' }}</p>
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.bank_branch') }}: {{ r.bank_branch || '—' }}</p>
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.account_number') }}: {{ r.account_number || '—' }}</p>
              <p class="text-xs text-muted mb-1">{{ $t('salary_management.iban') }}: {{ r.iban || '—' }}</p>
              <div class="d-flex flex-wrap gap-3 mt-2">
                <span class="text-xs"><strong>{{ $t('salary_management.total_lessons') }}:</strong> {{ r.total_lessons ?? '—' }}</span>
                <span class="text-xs"><strong>{{ $t('salary_management.attended_lessons') }}:</strong> {{ r.attended_lessons ?? '—' }}</span>
                <span class="text-xs"><strong>{{ $t('salary_management.absent_lessons') }}:</strong> {{ r.absent_lessons ?? '—' }}</span>
              </div>
              <p class="text-sm fw-bold mt-2 mb-1">{{ $t('salary_management.net_salary') }}: {{ formatDisplay(r.net_salary) }}</p>
              <p class="text-xs text-muted mb-0" v-if="r.notes">{{ $t('salary_management.notes') }}: {{ r.notes }}</p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import api from '@/api';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import { useTableExport, formatPrintDate } from '@/composables/useTableExport';

const { t } = useI18n();
const toast = useToast();
const { printTable, exportExcel, exportPdf } = useTableExport();

const monthFilter = ref(new Date().toISOString().slice(0, 7));
const isLoading = ref(false);
const exportingPdf = ref(false);
const rows = ref([]);

const toNumber = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const cleaned = String(v).replace(/[^\d.-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const formatDisplay = (v) => {
  const n = toNumber(v);
  return Number.isInteger(n) ? String(n) : String(n.toFixed(2)).replace(/\.?0+$/, '');
};

const fetchRows = async () => {
  if (!monthFilter.value) return;
  const [year, month] = monthFilter.value.split('-');
  isLoading.value = true;
  try {
    const res = await api.get('/salary-management/calculations', {
      params: { year, month: Number(month), type: 'teacher', all: true },
    });
    rows.value = res.data?.data || res.data || [];
  } catch (e) {
    toast.error(t('common.error'));
  } finally {
    isLoading.value = false;
  }
};

const reportMeta = () => {
  return [
    `${t('salary_management.month')}: ${monthFilter.value}`,
    `${t('salary_management.print_date')}: ${formatPrintDate()}`,
  ];
};

const columns = () => [
  { key: 'model_name', label: t('salary_management.teacher_name') },
  { key: 'job_title', label: t('salary_management.job_title') },
  { key: 'bank_name', label: t('salary_management.bank_name') },
  { key: 'bank_branch', label: t('salary_management.bank_branch') },
  { key: 'account_number', label: t('salary_management.account_number') },
  { key: 'iban', label: t('salary_management.iban') },
  { key: 'total_lessons', label: t('salary_management.total_lessons') },
  { key: 'attended_lessons', label: t('salary_management.attended_lessons') },
  { key: 'absent_lessons', label: t('salary_management.absent_lessons') },
  { key: 'net_salary', label: t('salary_management.net_salary'), format: (r) => formatDisplay(r.net_salary) },
  { key: 'notes', label: t('salary_management.notes') },
];

const onPrint = () => {
  printTable({
    title: t('salary_management.teacher_sheet_title'),
    subtitleLines: reportMeta(),
    columns: columns(),
    rows: rows.value,
  });
};

const onExportExcel = () => {
  exportExcel({
    title: t('salary_management.teacher_sheet_title'),
    filename: `teacher-salary-sheet-${monthFilter.value}`,
    columns: columns(),
    rows: rows.value,
  });
};

const onExportPdf = async () => {
  exportingPdf.value = true;
  try {
    await exportPdf({
      title: t('salary_management.teacher_sheet_title'),
      filename: `teacher-salary-sheet-${monthFilter.value}`,
      subtitleLines: reportMeta(),
      columns: columns(),
      rows: rows.value,
    });
  } catch (e) {
    toast.error(t('common.error'));
  } finally {
    exportingPdf.value = false;
  }
};

onMounted(fetchRows);
</script>
